/**
 * Panneau latéral du viewer — liste verticale des pièces du dossier.
 * Sprint 2 §1.2 : remplace la navigation prev/next par une vue "où en suis-je
 * dans la lecture du dossier ?".
 *
 *  - Pièce active mise en avant.
 *  - Indicateur visuel du review_status : ○ non vue · ● en cours · ✓ analysée · ⚠ à relire.
 *  - Compteur d'annotations sur la pièce.
 *  - Clic sur une ligne = ouverture du PDF correspondant.
 */
import type { InventorySourcePreference } from '../lib/sortPieces';
import type { Annotation, PdfMetadata } from '../lib/types';

type Props = {
  pieces: PdfMetadata[];
  activeId: string | undefined;
  annotations: Annotation[];
  onPick: (pdfId: string) => void;
  onClose?: () => void;
  // Sprint 2 §14 — optionnel : si fourni, ajoute un bouton corbeille
  // sur chaque ligne (visible au hover). Le parent gere la confirmation
  // et la navigation post-suppression.
  onDelete?: (pdf: PdfMetadata) => void;
  // Sprint 3+ (2026-05-21) — bouton ↗ Migrer vers un autre bucket.
  onMigrate?: (pdf: PdfMetadata) => void;
  // ADR-039 : permet de choisir la source d'ordre depuis le viewer
  // sans repasser par l'onglet Inventaire. Optionnel — si pas fourni,
  // pas de sélecteur.
  inventorySourcePref?: InventorySourcePreference | undefined;
  onChangeInventorySource?: ((pref: InventorySourcePreference) => void) | undefined;
};

const STATUS_GLYPH: Record<string, string> = {
  non_vue: '○',
  en_cours: '●',
  analysee: '✓',
  a_relire: '⚠',
};

const STATUS_LABEL: Record<string, string> = {
  non_vue: 'Non lue',
  en_cours: 'En cours',
  analysee: 'Analysée',
  a_relire: 'À relire',
};

const STATUS_COLOR: Record<string, string> = {
  non_vue: 'text-text-tertiary',
  en_cours: 'text-cat-imp-text',
  analysee: 'text-cat-fav',
  a_relire: 'text-cat-def',
};

export default function PiecesListPanel({
  pieces,
  activeId,
  annotations,
  onPick,
  onClose,
  onDelete,
  onMigrate,
  inventorySourcePref,
  onChangeInventorySource,
}: Props) {
  const annCountByPdf = new Map<string, number>();
  for (const a of annotations) {
    if (!a.pdf_file_id) continue;
    annCountByPdf.set(a.pdf_file_id, (annCountByPdf.get(a.pdf_file_id) ?? 0) + 1);
  }

  const total = pieces.length;
  const analysees = pieces.filter((p) => p.review_status === 'analysee').length;

  return (
    <aside
      className="flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-rule bg-paper-elevated"
      aria-label="Liste des pièces du dossier"
    >
      <header className="flex flex-col gap-1.5 border-b border-rule bg-paper-card px-3 py-2">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-medium uppercase tracking-[0.5px] text-text-secondary">
            Pièces · {analysees}/{total}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded p-0.5 text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
              title="Masquer le panneau"
              aria-label="Masquer le panneau"
            >
              ⮜
            </button>
          )}
        </div>
        {onChangeInventorySource && inventorySourcePref && (
          <label className="flex items-center gap-1.5 text-[10.5px] text-text-tertiary">
            <span>Ordre :</span>
            <select
              value={inventorySourcePref}
              onChange={(e) => onChangeInventorySource(e.target.value as InventorySourcePreference)}
              className="flex-1 rounded border border-rule bg-paper-elevated px-1 py-0.5 text-[10.5px]"
              title="Ordre des pièces — basé sur l'inventaire"
            >
              <option value="auto">Inventaire (auto)</option>
              <option value="manual">Inventaire manuel</option>
              <option value="ai_parsed">Inventaire IA</option>
            </select>
          </label>
        )}
      </header>
      <ul className="flex-1 overflow-y-auto">
        {pieces.map((p, i) => {
          const isActive = p.id === activeId;
          const status = p.review_status ?? 'non_vue';
          const annCount = annCountByPdf.get(p.id) ?? 0;
          return (
            <li key={p.id} className="group relative">
              <button
                type="button"
                onClick={() => onPick(p.id)}
                className={`flex w-full items-start gap-2 border-b border-rule-soft px-3 py-2 text-left text-[12px] last:border-b-0 ${
                  isActive
                    ? 'bg-accent-lighter text-accent-text'
                    : 'text-text-primary hover:bg-paper-card'
                }`}
                title={`${STATUS_LABEL[status] ?? status} — ${p.filename_original}`}
              >
                <span
                  className={`mt-0.5 inline-block w-4 text-center font-mono text-[13px] leading-none ${
                    isActive ? 'text-accent-text' : (STATUS_COLOR[status] ?? 'text-text-tertiary')
                  }`}
                  aria-hidden="true"
                >
                  {STATUS_GLYPH[status] ?? '○'}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-mono text-[11px] text-text-tertiary">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span
                    className={`block truncate ${isActive ? 'font-medium' : ''}`}
                    style={{ wordBreak: 'break-word' }}
                  >
                    {p.filename_original}
                  </span>
                  {annCount > 0 && (
                    <span className="mt-0.5 inline-block text-[10px] text-accent-text">
                      {annCount} annotation{annCount > 1 ? 's' : ''}
                    </span>
                  )}
                </span>
              </button>
              {onMigrate && (
                <button
                  type="button"
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    onMigrate(p);
                  }}
                  className="absolute right-7 top-1.5 rounded border border-accent/30 bg-accent-light px-1 py-0 text-[10.5px] text-accent-text transition hover:bg-accent hover:text-paper-elevated"
                  title="Migrer vers un autre bucket"
                  aria-label={`Migrer la pièce ${p.filename_original}`}
                >
                  ↗
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    onDelete(p);
                  }}
                  className="absolute right-1.5 top-1.5 rounded p-0.5 text-text-tertiary opacity-0 transition group-hover:opacity-100 hover:bg-cat-def-pastel hover:text-cat-def"
                  title="Supprimer la pièce"
                  aria-label={`Supprimer la pièce ${p.filename_original}`}
                >
                  🗑
                </button>
              )}
            </li>
          );
        })}
        {pieces.length === 0 && (
          <li className="px-3 py-4 text-center text-[12px] text-text-tertiary">Aucune pièce</li>
        )}
      </ul>
    </aside>
  );
}
